import { baseApi, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import type {
  JournalJob,
  MyShift,
  ReceiptDetail,
  ReceiptPage,
  ReceiptRegisters,
  ReceiptShifts,
  ReceiptsFilter,
  ReceiptSummary,
} from '../model/types'
import {
  decodeJournalJob,
  decodeMyShifts,
  decodeReceiptDetail,
  decodeReceiptPage,
  decodeReceiptRegisters,
  decodeReceiptShifts,
  decodeReceiptSummary,
} from './contracts'
import { AQSI_RECEIPT_URLS } from './urls'

/**
 * Endpoints журнала чеков в ОБЩЕМ `baseApi` (R04): имена `aqsi*`, rid — аргумент
 * каждого запроса (ключ кэша и тегов), поэтому два AQSI с одинаковыми номерами
 * касс и чеков получают разные записи кэша, а сброс одного не стирает второй.
 */
export const AQSI_RECEIPT_TAG = 'AqsiReceipt' as const
export const AQSI_RECEIPT_JOURNAL_TAG = 'AqsiReceiptJournal' as const

/** Размер страницы журнала: предел договора 200, по умолчанию 50. */
const RECEIPTS_PAGE_SIZE = 50

export interface ReceiptListArgs {
  resourceId: string
  filter: ReceiptsFilter
  /** Курсор страницы; `null` — первая страница. */
  cursor: string | null
}
/** Итоги по отбору: тот же отбор, что у списка, но без вида чека — приход и возвраты считаются оба. */
export interface ReceiptSummaryArgs {
  resourceId: string
  filter: ReceiptsFilter
}
export interface ReceiptArgs {
  resourceId: string
  receiptId: string
}
export interface RegistersArgs {
  resourceId: string
}
export interface ShiftsArgs {
  resourceId: string
  registerId: string
  detailsFor?: string
}
/** Период «Моих смен»: моменты RFC 3339 с поясом (оба обязательны, не больше 366 суток). */
export interface MyShiftsArgs {
  resourceId: string
  from: string
  to: string
}
export interface JournalSyncArgs {
  resourceId: string
  operationId: string
  idempotencyKey: string
}

const orUndefined = (value: string): string | undefined => (value.trim() === '' ? undefined : value.trim())

const pad = (value: number, width = 2): string => String(Math.trunc(Math.abs(value))).padStart(width, '0')

/**
 * Момент по часам браузера в RFC 3339 со СМЕЩЕНИЕМ браузера (`2026-10-07T00:00:00.000+03:00`), а не в UTC: по смещению начала периода
 * сервер раскладывает чеки по дням. С `Z` дни считались по UTC, и чеки с 00:00 до 03:00 МСК попадали в предыдущий день.
 */
function localInstant(moment: Date): string {
  const minutes = -moment.getTimezoneOffset()
  const sign = minutes < 0 ? '-' : '+'
  return (
    `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}` +
    `T${pad(moment.getHours())}:${pad(moment.getMinutes())}:${pad(moment.getSeconds())}.${pad(moment.getMilliseconds(), 3)}` +
    `${sign}${pad(minutes / 60)}:${pad(minutes % 60)}`
  )
}

/** Начало дня по часам браузера, RFC 3339 со смещением браузера (сервер требует пояс). */
export function periodStart(date: string): string | undefined {
  return date === '' ? undefined : localInstant(new Date(`${date}T00:00:00`))
}

/** Конец дня по часам браузера. */
export function periodEnd(date: string): string | undefined {
  return date === '' ? undefined : localInstant(new Date(`${date}T23:59:59.999`))
}

/** Параметры отбора, общие у списка и итогов. */
function filterParams(filter: ReceiptsFilter) {
  return {
    registerId: filter.serials === undefined ? orUndefined(filter.registerId) : undefined,
    shop: filter.serials === undefined ? orUndefined(filter.shop) : undefined,
    serials: filter.serials,
    cashier: orUndefined(filter.cashier),
    shiftNumber: orUndefined(filter.shiftNumber),
    from: periodStart(filter.dateFrom),
    to: periodEnd(filter.dateTo),
    search: orUndefined(filter.search),
  }
}

export const receiptsApi = baseApi
  .enhanceEndpoints({ addTagTypes: [AQSI_RECEIPT_TAG, AQSI_RECEIPT_JOURNAL_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      aqsiReceipts: build.query<ReceiptPage, ReceiptListArgs>({
        query: ({ resourceId, filter, cursor }) => ({
          url: AQSI_RECEIPT_URLS.list(resourceId),
          params: {
            ...filterParams(filter),
            kind: orUndefined(filter.kind),
            cursor: cursor ?? undefined,
            pageSize: RECEIPTS_PAGE_SIZE,
          },
          decode: (raw) => decodeReceiptPage(raw, resourceId),
        }),
        providesTags: (_result, _error, { resourceId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, 'LIST'),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiReceiptSummary: build.query<ReceiptSummary, ReceiptSummaryArgs>({
        query: ({ resourceId, filter }) => ({
          url: AQSI_RECEIPT_URLS.summary(resourceId),
          params: filterParams(filter),
          decode: (raw) => decodeReceiptSummary(raw, resourceId),
        }),
        providesTags: (_result, _error, { resourceId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, 'SUMMARY'),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiReceipt: build.query<ReceiptDetail, ReceiptArgs>({
        query: ({ resourceId, receiptId }) => ({
          url: AQSI_RECEIPT_URLS.one(resourceId, receiptId),
          decode: (raw) => decodeReceiptDetail(raw, resourceId, receiptId),
        }),
        providesTags: (_result, _error, { resourceId, receiptId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, receiptId),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiReceiptRegisters: build.query<ReceiptRegisters, RegistersArgs>({
        query: ({ resourceId }) => ({
          url: AQSI_RECEIPT_URLS.registers(resourceId),
          decode: (raw) => decodeReceiptRegisters(raw, resourceId),
        }),
        providesTags: (_result, _error, { resourceId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, 'REGISTERS'),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiReceiptShifts: build.query<ReceiptShifts, ShiftsArgs>({
        query: ({ resourceId, registerId, detailsFor }) => ({
          url: AQSI_RECEIPT_URLS.shifts(resourceId, registerId),
          params: { detailsFor: detailsFor || undefined },
          decode: (raw) => decodeReceiptShifts(raw, resourceId, registerId),
        }),
        providesTags: (_result, _error, { resourceId, registerId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, `SHIFTS:${registerId}`),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiMyShifts: build.query<readonly MyShift[], MyShiftsArgs>({
        query: ({ resourceId, from, to }) => ({
          url: AQSI_RECEIPT_URLS.myShifts(resourceId),
          params: { from, to },
          decode: (raw) => decodeMyShifts(raw, resourceId),
        }),
        providesTags: (_result, _error, { resourceId }) => [
          resourceTag(AQSI_RECEIPT_TAG, resourceId, 'MY-SHIFTS'),
          resourceScopeTag(resourceId),
        ],
      }),
      aqsiReceiptJournal: build.query<JournalJob | null, RegistersArgs>({
        query: ({ resourceId }) => ({
          url: AQSI_RECEIPT_URLS.journalStatus(resourceId),
          decode: (raw) => decodeJournalJob(raw, resourceId),
        }),
        providesTags: (_result, _error, { resourceId }) => [
          resourceTag(AQSI_RECEIPT_JOURNAL_TAG, resourceId, 'STATUS'),
          resourceScopeTag(resourceId),
        ],
      }),
      /**
       * Поставить сверку журнала. 202 — снимок долговечного задания, а не
       * доказательство успеха; результат читается из `aqsiReceiptJournal`.
       * Повтор той же команды безопасен (operationId + ключ), но ретраев нет.
       */
      aqsiReceiptJournalSync: build.mutation<JournalJob | null, JournalSyncArgs>({
        query: ({ resourceId, operationId, idempotencyKey }) => ({
          url: AQSI_RECEIPT_URLS.journalSync(resourceId),
          method: 'POST',
          idempotencyKey,
          body: {
            kind: 'aqsi.receipt-journal.sync',
            resourceId,
            contractVersion: '1.0',
            schemaVersion: '1.0-draft.2',
            operationId,
            idempotencyKey,
          },
          decode: (raw) => decodeJournalJob(raw, resourceId),
        }),
        invalidatesTags: (_result, _error, { resourceId }) => [resourceTag(AQSI_RECEIPT_JOURNAL_TAG, resourceId, 'STATUS')],
      }),
    }),
  })

export const {
  aqsiReceipts,
  aqsiReceiptSummary,
  aqsiReceipt,
  aqsiReceiptRegisters,
  aqsiReceiptShifts,
  aqsiReceiptJournal,
  aqsiReceiptJournalSync,
  aqsiMyShifts,
} = receiptsApi.endpoints
